
import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, EMPTY, tap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { NetworkService } from '../services/network.service';

import {
  LOGIN,
  GET_PUBLIC_COMPANY,
  GET_PUBLIC_COMPANY_LOGO
} from '../../constant';
import { environment } from '../../../environments/environment';

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const network = inject(NetworkService);

  const token = auth.getToken();

  // Identification des endpoints accessibles sans authentification.
  const pathname = new URL(req.url, environment.apiUrl).pathname;

  const isPublicRequest =
    (req.method === 'GET' && (
      pathname === GET_PUBLIC_COMPANY ||
      pathname === GET_PUBLIC_COMPANY_LOGO
    )) ||
    (req.method === 'POST' && pathname === LOGIN);

  // Les requêtes publiques passent sans JWT.
  // Un token expiré ne doit pas empêcher leur exécution.
  if (isPublicRequest) {
    return next(req).pipe(
      tap(() => network.setOnline()),

      catchError((error: HttpErrorResponse) => {
        if (error.status === 0) {
          network.setOffline();
          return EMPTY;
        }

        network.setOnline();
        return throwError(() => error);
      })
    );
  }

  // Pour les autres requêtes, conserver la vérification du JWT.
  if (token && auth.isTokenExpired(token)) {
    auth.logout('expired');
    router.navigate(['/login']);
    return EMPTY;
  }

  const request = token
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      })
    : req;

  return next(request).pipe(
    tap(() => network.setOnline()),

    catchError((error: HttpErrorResponse) => {
      // API / réseau inaccessible
      if (error.status === 0) {
        network.setOffline();
        return EMPTY;
      }

      // Le serveur a répondu : le réseau/API est accessible.
      network.setOnline();

      // Problème d'authentification
      if (error.status === 401 && token) {
        auth.logout(auth.isTokenExpired(token) ? 'expired' : 'invalid');
        router.navigate(['/login']);
      }

      return throwError(() => error);
    })
  );
};
